import { BUG_LIMITS, trailText, type BugContext, type BugTrailEvent } from '@fieldops/shared'
import { CLIENT_BUILD } from '../lib/release.js'

/**
 * What the screen was doing before somebody reported a bug. Decision 117.
 *
 * Installed once, before the app draws, and kept in memory only: the last
 * clicks, the fields changed (named, never read), the screens visited,
 * console messages and errors, and requests. Nothing leaves the device until
 * a report is sent, and then only what `bugTrailSnapshot` hands over.
 *
 * Each kind has its own ring, so a console that logs in a loop cannot push
 * the clicks that led to the bug out of the trail.
 */

const KEEP: Record<BugTrailEvent['kind'], number> = {
  click: 30,
  input: 20,
  nav: 30,
  console: 60,
  error: 20,
  request: 40,
}

/** A report's trail, as JSON, stays well inside the upload's field limit. */
const MAX_TRAIL_CHARS = 600_000

const rings = new Map<BugTrailEvent['kind'], BugTrailEvent[]>()
let installed = false
let paused = 0
const loadedAt = Date.now()

function keep(event: BugTrailEvent) {
  if (paused > 0) return
  const ring = rings.get(event.kind) ?? []
  ring.push(event)
  if (ring.length > KEEP[event.kind]) ring.shift()
  rings.set(event.kind, ring)
}

/** The path and query of a URL on this site; another site keeps its host. */
function shortUrl(raw: string): string {
  try {
    const url = new URL(raw, window.location.href)
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`
    return `${url.host}${url.pathname}`
  } catch {
    return raw
  }
}

const here = () => {
  const { pathname, search } = window.location
  return `${pathname}${search}`.slice(0, 300)
}

const squash = (value: string | null | undefined, max = 80) => {
  const text = (value ?? '').replace(/\s+/g, ' ').trim()
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

/* -------------------------------------------------------------------------- */
/* Clicks and fields                                                          */
/* -------------------------------------------------------------------------- */

const ACTIONABLE = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[role="button"]',
  '[role="link"]',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  '[role="tab"]',
  '[role="option"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="gridcell"]',
  '[role="row"]',
  '[data-guide]',
  '[data-testid]',
].join(',')

function textOf(ids: string | null): string {
  if (!ids) return ''
  return ids
    .split(/\s+/)
    .map((id) => document.getElementById(id)?.textContent ?? '')
    .join(' ')
}

function roleOf(element: Element): string {
  const role = element.getAttribute('role')
  if (role) return role
  const tag = element.tagName.toLowerCase()
  if (tag === 'a') return 'link'
  if (tag === 'select') return 'list'
  if (tag === 'textarea') return 'text box'
  if (tag === 'input') {
    const type = (element as HTMLInputElement).type
    if (type === 'checkbox' || type === 'radio') return type
    if (type === 'button' || type === 'submit') return 'button'
    return 'field'
  }
  return tag
}

/**
 * A field's name, never its value: the label, the aria name, a placeholder
 * or the `name` attribute.
 */
function fieldName(element: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement): string {
  const label =
    element.getAttribute('aria-label') ||
    textOf(element.getAttribute('aria-labelledby')) ||
    (element.id ? document.querySelector(`label[for="${CSS.escape(element.id)}"]`)?.textContent : '') ||
    element.closest('label')?.textContent ||
    ('placeholder' in element ? element.placeholder : '') ||
    element.name
  return squash(label)
}

function nameOf(element: Element): string {
  if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement) {
    return fieldName(element)
  }
  return squash(
    element.getAttribute('aria-label') ||
      textOf(element.getAttribute('aria-labelledby')) ||
      element.getAttribute('title') ||
      (element as HTMLElement).innerText ||
      element.getAttribute('alt') ||
      '',
  )
}

function describe(element: Element): { target: string; detail?: string } {
  const role = roleOf(element)
  const name = nameOf(element)
  const target = name ? `${role} “${name}”` : role
  const hints: string[] = []
  const guide = element.closest('[data-guide]')?.getAttribute('data-guide')
  if (guide) hints.push(`data-guide=${guide}`)
  const dialog = element.closest('[role="dialog"]')
  if (dialog) {
    const title = squash(dialog.getAttribute('aria-label') || textOf(dialog.getAttribute('aria-labelledby')), 60)
    hints.push(title ? `in the “${title}” window` : 'in a window')
  }
  return hints.length ? { target, detail: hints.join(', ') } : { target }
}

/** Not the report's own controls: a trail that ends in "clicked Send" says nothing. */
const ours = (element: Element) => Boolean(element.closest('[data-bug-capture]'))

function onClick(event: MouseEvent) {
  const start = event.target instanceof Element ? event.target : null
  if (!start || ours(start)) return
  const element = start.closest(ACTIONABLE) ?? start
  keep({ t: Date.now(), kind: 'click', ...describe(element), path: here() })
}

function onChange(event: Event) {
  const element = event.target
  if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement)) return
  if (ours(element)) return
  let value: string | undefined
  if (element instanceof HTMLSelectElement) value = squash(element.selectedOptions[0]?.textContent, 60)
  else if (element instanceof HTMLInputElement && (element.type === 'checkbox' || element.type === 'radio')) {
    value = element.checked ? 'on' : 'off'
  }
  // A typed value is never kept: only that the field changed.
  keep({ t: Date.now(), kind: 'input', target: `${roleOf(element)} “${fieldName(element)}”`, ...(value ? { value } : {}), path: here() })
}

/* -------------------------------------------------------------------------- */
/* Screens                                                                    */
/* -------------------------------------------------------------------------- */

let lastNav = ''
function onNavigate() {
  const to = `${window.location.pathname}${window.location.search}${window.location.hash}`
  if (to === lastNav) return
  lastNav = to
  keep({ t: Date.now(), kind: 'nav', to: to.slice(0, 500) })
}

/* -------------------------------------------------------------------------- */
/* Console and errors                                                         */
/* -------------------------------------------------------------------------- */

function stackOf(error: Error): string | undefined {
  return error.stack?.split('\n').slice(0, 8).join('\n')
}

function asText(value: unknown): string {
  if (typeof value === 'string') return value
  if (value instanceof Error) return `${value.name}: ${value.message}`
  if (value === undefined) return 'undefined'
  try {
    const seen = new WeakSet<object>()
    return JSON.stringify(value, (_key, item: unknown) => {
      if (typeof item === 'object' && item !== null) {
        if (seen.has(item)) return '[circular]'
        seen.add(item)
        if (item instanceof Element) return `<${item.tagName.toLowerCase()}>`
      }
      return typeof item === 'bigint' ? String(item) : item
    }) ?? String(value)
  } catch {
    return String(value)
  }
}

function wrapConsole(level: 'error' | 'warn' | 'info' | 'log') {
  const original = console[level].bind(console)
  console[level] = (...args: unknown[]) => {
    try {
      const error = args.find((arg): arg is Error => arg instanceof Error)
      const message = args.map(asText).join(' ')
      keep({
        t: Date.now(),
        kind: 'console',
        level,
        message: trailText(error?.stack ? `${message}\n${stackOf(error)}` : message, 1500),
      })
    } catch {
      // The recorder never breaks the console it is listening to.
    }
    original(...args)
  }
}

function onError(event: Event) {
  if (event instanceof ErrorEvent) {
    keep({
      t: Date.now(),
      kind: 'error',
      message: trailText(event.message || 'Script error', 1000),
      ...(event.error instanceof Error && event.error.stack ? { stack: stackOf(event.error) } : {}),
      ...(event.filename ? { source: `${shortUrl(event.filename)}:${event.lineno}:${event.colno}` } : {}),
    })
    return
  }
  // A picture, script or stylesheet that would not load.
  const element = event.target
  if (element instanceof HTMLImageElement || element instanceof HTMLScriptElement || element instanceof HTMLLinkElement) {
    const source = element instanceof HTMLLinkElement ? element.href : element.src
    if (!source || ours(element)) return
    keep({ t: Date.now(), kind: 'error', message: `Could not load ${element.tagName.toLowerCase()} ${shortUrl(source)}` })
  }
}

function onRejection(event: PromiseRejectionEvent) {
  const reason: unknown = event.reason
  keep({
    t: Date.now(),
    kind: 'error',
    message: trailText(`Unhandled: ${asText(reason)}`, 1000),
    ...(reason instanceof Error && reason.stack ? { stack: stackOf(reason) } : {}),
  })
}

/* -------------------------------------------------------------------------- */
/* Requests                                                                   */
/* -------------------------------------------------------------------------- */

/** A failed tRPC batch names each procedure that failed and what it said. */
export function trpcErrors(body: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(body)
    const items = Array.isArray(parsed) ? parsed : [parsed]
    const said = items.flatMap((item) => {
      const error = (item as { error?: { json?: { message?: string; data?: { path?: string; code?: string } } } })?.error?.json
      if (!error?.message) return []
      const where = error.data?.path ? `${error.data.path}: ` : ''
      return [`${where}${error.data?.code ? `${error.data.code} ` : ''}${error.message}`]
    })
    if (said.length) return said.join(' | ')
    const plain = (parsed as { error?: unknown })?.error
    return typeof plain === 'string' ? plain : undefined
  } catch {
    return body ? body.slice(0, 300) : undefined
  }
}

function wrapFetch() {
  const original = window.fetch.bind(window)
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const started = performance.now()
    const t = Date.now()
    const raw = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const method = (init?.method ?? (input instanceof Request ? input.method : 'GET')).toUpperCase()
    let sameSite = true
    try {
      sameSite = new URL(raw, window.location.href).origin === window.location.origin
    } catch {
      // A URL the browser cannot parse fails in fetch itself, below.
    }
    // The query is dropped: a batched read carries what was searched for.
    const url = shortUrl(raw).split('?')[0]!.slice(0, 300)
    try {
      const response = await original(input, init)
      const ms = Math.round(performance.now() - started)
      // Map tiles and fonts from elsewhere are noise unless they fail.
      if (sameSite || !response.ok) {
        const event: Extract<BugTrailEvent, { kind: 'request' }> = { t, kind: 'request', method, url, status: response.status, ms }
        keep(event)
        if (!response.ok) {
          response
            .clone()
            .text()
            .then((body) => {
              const said = trpcErrors(body)
              if (said) event.error = trailText(said, 500)
            })
            .catch(() => undefined)
        }
      }
      return response
    } catch (error) {
      keep({
        t,
        kind: 'request',
        method,
        url,
        status: null,
        ms: Math.round(performance.now() - started),
        error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
      })
      throw error
    }
  }
}

/* -------------------------------------------------------------------------- */
/* Installing and reading                                                     */
/* -------------------------------------------------------------------------- */

export function installBugRecorder(): void {
  if (installed || typeof window === 'undefined') return
  installed = true

  document.addEventListener('click', onClick, { capture: true, passive: true })
  document.addEventListener('change', onChange, { capture: true, passive: true })
  window.addEventListener('error', onError, { capture: true })
  window.addEventListener('unhandledrejection', onRejection)

  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method].bind(history)
    history[method] = (...args: Parameters<History['pushState']>) => {
      original(...args)
      onNavigate()
    }
  }
  window.addEventListener('popstate', onNavigate)
  onNavigate()

  for (const level of ['error', 'warn', 'info', 'log'] as const) wrapConsole(level)
  wrapFetch()
}

/** The trail, oldest first, cut to what a report can carry. */
export function bugTrailSnapshot(): BugTrailEvent[] {
  const all = [...rings.values()].flat().sort((a, b) => a.t - b.t)
  let trail = all.slice(-BUG_LIMITS.trailEvents)
  // The console is the first to go when the whole is too big.
  while (JSON.stringify(trail).length > MAX_TRAIL_CHARS) {
    const oldestConsole = trail.findIndex((event) => event.kind === 'console')
    trail = oldestConsole >= 0 ? trail.filter((_event, index) => index !== oldestConsole) : trail.slice(1)
  }
  return trail.map((event) => ({ ...event }))
}

/**
 * Runs `work` without recording it: taking the picture fetches every font and
 * image on the page again, and none of that is what the person did.
 */
export async function unrecorded<T>(work: () => Promise<T>): Promise<T> {
  paused += 1
  try {
    return await work()
  } finally {
    paused -= 1
  }
}

/** Forgets everything; for tests. */
export function clearBugTrail(): void {
  rings.clear()
}

const FIELD_QUERY = '(max-width: 1023px), (pointer: coarse)'

/**
 * The screen's name as the rail or the tabs say it ("Board", "Jobs"), and
 * the open job or window when there is one. The page title is always EcoPest.
 */
function screenName(): string {
  const current = squash(document.querySelector('nav [aria-current="page"]')?.textContent, 60)
  const job = new URLSearchParams(window.location.search).get('jobId') ? 'a job' : ''
  const parts = [current || squash(document.title, 60), job].filter(Boolean)
  return parts.join(', with ')
}

/** The device and copy of the app, at the moment of the report. */
export function bugContextSnapshot(picture: BugContext['picture']): BugContext {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string }
    connection?: { effectiveType?: string }
  }
  const memory = (performance as Performance & { memory?: { usedJSHeapSize?: number } }).memory?.usedJSHeapSize
  return {
    url: here(),
    title: screenName(),
    userAgent: navigator.userAgent,
    platform: nav.userAgentData?.platform || navigator.platform || undefined,
    language: navigator.language,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: { width: window.screen?.width ?? 0, height: window.screen?.height ?? 0 },
    pixelRatio: window.devicePixelRatio || 1,
    field: window.matchMedia?.(FIELD_QUERY).matches ?? false,
    standalone: window.matchMedia?.('(display-mode: standalone)').matches ?? false,
    online: navigator.onLine,
    ...(nav.connection?.effectiveType ? { connection: nav.connection.effectiveType } : {}),
    ...(CLIENT_BUILD ? { clientBuild: CLIENT_BUILD } : {}),
    uptimeMs: Date.now() - loadedAt,
    ...(memory ? { memoryMb: Math.round(memory / 1024 / 1024) } : {}),
    picture,
  }
}
